import React, { useEffect, useState } from "react";
import { Card, CardBody, Row, Col, Button, Badge } from "reactstrap";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { loadingTrue, loadingFalse } from "../../Reducer/loaderSlice";
import Handler from "../../Helpers/Handler";
import { toast } from "react-toastify";
import "boxicons/css/boxicons.min.css";

const ProductView = () => {

  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [product, setProduct] = useState(null);

  const load = async () => {
    dispatch(loadingTrue());
    try {
      const res = await Handler({
        method: "get",
        url: `/products/view.php?id=${id}`
      });
      if (res?.success) setProduct(res.data);
      else toast.error(res?.message || "Failed to fetch product");
    } catch {
      toast.error("Network error");
    }
    dispatch(loadingFalse());
  };

  useEffect(() => { load(); }, [id]);

  if (!product) return null;

  const meta = product.meta || {};

  const stockColor = (qty, limit) => {
    if (qty <= 0) return "danger";
    if (limit !== null && qty <= limit) return "warning";
    return "success";
  };

  return (
    <>
      {/* ================= HEADER ================= */}
      <Row className="mb-4 align-items-center">
        <Col>
          <h4 className="fw-bold mb-1 text-capitalize">{product.name}</h4>
          <small className="text-muted">
            {product.category_name} / {product.sub_category_name}
          </small>
        </Col>
        <Col className="text-end d-flex justify-content-end gap-2">
          <Button color="light" onClick={() => navigate(`/products-edit/${product.product_id}`)}>
            <i className="bx bx-edit"></i> Edit
          </Button>
          <Button color="warning" onClick={() => navigate(-1)}>
            <i className="bx bx-arrow-back"></i> Back
          </Button>
        </Col>
      </Row>

      {/* ================= PRODUCT INFO ================= */}
      <Card className="mb-4">
        <CardBody>
          <h6 className="fw-bold mb-3">Product Information</h6>

          <Row>
            <Col md={3}>
              <small className="text-muted">Selling Price</small>
              <h6>₹ {Number(product.price).toFixed(2)}</h6>
            </Col>

            <Col md={3}>
              <small className="text-muted">GST %</small>
              <h6>{product.gst_rate}%</h6>
            </Col>

            <Col md={3}>
              <small className="text-muted">Stock</small>
              <h6>
                <Badge color={stockColor(product.quantity, product.low_stock_limit)}>
                  {product.quantity}
                </Badge>
              </h6>
              {product.low_stock_limit !== null && (
                <small className="text-muted">
                  Low stock ≤ {product.low_stock_limit}
                </small>
              )}
            </Col>

            <Col md={3}>
              <small className="text-muted">Barcode</small>
              <h6>{product.barcode || "-"}</h6>
            </Col>
          </Row>
        </CardBody>
      </Card>

      {/* ================= META INFO ================= */}
      <Card className="mb-4">
        <CardBody>
          <h6 className="fw-bold mb-3">Meta Information</h6>

          <Row>
            <Col md={3}>
              <small className="text-muted">SKU</small>
              <div>{meta.sku || "-"}</div>
            </Col>

            <Col md={3}>
              <small className="text-muted">Dealer</small>
              <div>{meta.dealer || "-"}</div>
            </Col>

            <Col md={3}>
              <small className="text-muted">Unit / Size</small>
              <div>{meta.size || "-"}</div>
            </Col>

            <Col md={3}>
              <small className="text-muted">Purchase Price</small>
              <div>₹ {Number(meta.purchase_price).toFixed(2) || "-"}</div>
            </Col>
          </Row>

          <Row className="mt-3">
            <Col>
              <small className="text-muted">Description</small>
              <div>{meta.description || "-"}</div>
            </Col>
          </Row>
        </CardBody>
      </Card>

      {/* ================= VARIANTS ================= */}
      <Card>
        <CardBody>
          <h6 className="fw-bold mb-3">Variants</h6>

          {product.variants?.length ? (
            product.variants.map(v => (
              <Card key={v.variant_id} className="mb-2">
                <CardBody className="py-2">
                  <Row className="align-items-center">
                    <Col md={3}>
                      <div className="fw-semibold">{v.name}</div>
                      <small className="text-muted">
                        Barcode: {v.barcode || "-"}
                      </small>
                    </Col>

                    <Col md={2}>
                      <small className="text-muted">Price</small>
                      <div>₹ {Number(v.price).toFixed(2)}</div>
                    </Col>

                    <Col md={2}>
                      <small className="text-muted">GST</small>
                      <div>{v.gst_rate}%</div>
                    </Col>

                    <Col md={3}>
                      <small className="text-muted">Stock</small>
                      <div>
                        <Badge color={stockColor(v.quantity, v.low_stock_limit)}>
                          {v.quantity}
                        </Badge>
                        {v.low_stock_limit !== null && (
                          <small className="text-muted ms-2">
                            (≤ {v.low_stock_limit})
                          </small>
                        )}
                      </div>
                    </Col>

                    <Col md={2} className="text-end">
                      <Button
                        size="sm"
                        color="light"
                        onClick={() =>
                          navigate(`/products-edit/${product.product_id}?variant=${v.variant_id}`)
                        }
                      >
                        <i className="bx bx-edit"></i>
                      </Button>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            ))
          ) : (
            <div className="text-muted text-center py-3">
              No variants available
            </div>
          )}
        </CardBody>
      </Card>
    </>
  );
};

export default ProductView;
